"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function truncateAddress(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}\u2026${addr.slice(-4)}`;
}

// ---------------------------------------------------------------------------
// CopyButton
// ---------------------------------------------------------------------------

function CopyButton({
  text,
  label,
  className,
}: {
  text: string;
  label: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { setCopied(false); }, 1500);
    } catch {
      // Clipboard API unavailable — silently ignore
    }
  }, [text]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      className={cn(
        "inline-flex items-center gap-1 text-[11px] transition-colors duration-150 rounded",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]",
        className,
      )}
      style={{ color: copied ? "var(--green)" : "var(--text-secondary)" }}
      aria-label={copied ? "Copied" : label}
    >
      {copied ? (
        <Check size={11} aria-hidden="true" />
      ) : (
        <Copy size={11} aria-hidden="true" />
      )}
      <span>{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// AddressRow
// ---------------------------------------------------------------------------

function AddressRow({
  label,
  address,
  explorerUrl,
}: {
  label: string;
  address: string;
  explorerUrl: string;
}) {
  return (
    <div className="px-5 py-3" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <p
        className="text-[9px] tracking-[0.1em] uppercase font-medium mb-1.5"
        style={{ color: "var(--text-secondary)" }}
      >
        {label}
      </p>
      <div className="flex items-center gap-2">
        <p
          className="text-[11px] font-mono flex-1 min-w-0 truncate"
          style={{ color: "var(--text-secondary)" }}
          title={address}
        >
          {truncateAddress(address)}
        </p>
        <CopyButton text={address} label={`Copy ${label.toLowerCase()}`} className="flex-shrink-0" />
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-70 transition-opacity rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]"
          aria-label={`View ${label.toLowerCase()} on Stellar Expert`}
        >
          <ExternalLink size={12} style={{ color: "var(--text-tertiary)" }} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SmartAccountCard
// ---------------------------------------------------------------------------

interface SmartAccountCardProps {
  smartAccountAddress: string;
}

export function SmartAccountCard({ smartAccountAddress }: SmartAccountCardProps) {
  // Smart accounts are Soroban contracts (C... addresses), not classic accounts
  const accountExplorerUrl = `https://stellar.expert/explorer/testnet/contract/${smartAccountAddress}`;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: "var(--surface)", border: "1px solid var(--border-default)" }}
      aria-label="My smart account"
    >
      {/* Card header */}
      <div
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        <h2
          className="font-serif font-normal"
          style={{ fontSize: "1rem", color: "var(--text-primary)" }}
        >
          My Smart Account
        </h2>

        {/* Testnet badge */}
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full"
          style={{
            background: "rgba(251, 191, 36, 0.1)",
            border: "1px solid rgba(251, 191, 36, 0.2)",
            color: "#fbbf24",
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: "#fbbf24" }}
            aria-hidden="true"
          />
          Testnet
        </span>
      </div>

      {/* Primary address */}
      <AddressRow
        label="Primary Address"
        address={smartAccountAddress}
        explorerUrl={accountExplorerUrl}
      />
    </div>
  );
}
